import type { GameId, PlayerId, ProfileId, SkillBand } from './ids';
import type { PlayerMode } from './players';

/**
 * Who launched the sitting (D-054(2)).
 *
 * `SessionLaunchConfig` is deliberately session-only and stays that way: a level picked because a
 * child is tired tonight, or narration turned off because a baby is asleep, is a property of the
 * evening and not a statement about anybody. But a browser reload also emptied the one thing that
 * *is* a fact about the sitting — which children were in the room — and without it the resume
 * guard could not prove a saved mission belonged to whoever was now at the screen, so a named or
 * cooperative sitting started over.
 *
 * This record is that fact and nothing else: the game, the mode, and one seat per hero in lane
 * order. No narration setting, no volume, no reduced motion, no in-game name a child typed, no
 * timestamps. D-054(2) is explicit on both halves — "persist identity, not preferences", and
 * "profile ids only, no names beyond what profiles already store".
 *
 * **Why this is a separate record from the mission snapshot.** Rebuilding the roster from the
 * snapshot's own `players` would make `canResumeMissionProgress` compare the snapshot with itself,
 * so it would pass unconditionally and the sibling/guest refusals (D-036(4), PS2-8) would quietly
 * stop meaning anything. This is independent evidence, so the guard still has two things to
 * compare and every refusal it makes is still a real one.
 */

/**
 * The lane player ids, declared here rather than imported.
 *
 * The storage layer may not import the input layer, and these ids belong to the lanes rather than
 * to the keyboard adapter that happens to define them today. Pinned against
 * `HERO_1_PLAYER_ID`/`HERO_2_PLAYER_ID` by a test, so the duplication cannot drift silently.
 */
export const LAUNCH_IDENTITY_LANE_PLAYER_IDS = [
  'hero-1',
  'hero-2',
] as const satisfies readonly PlayerId[];

export interface LaunchIdentitySeat {
  /** Must be the lane id for this seat's index; lanes are fixed for a whole mission (COOP-08). */
  playerId: PlayerId;
  /** The band this sitting was played at, which may differ from the child's stored band. */
  band: SkillBand;
  /**
   * Explicit rather than inferred from `profileId`'s absence.
   *
   * "Not a guest, but nobody in particular" is exactly the identity a resume must never guess at,
   * so the two fields have to agree: `isGuest` true means no `profileId`, false means one is
   * present. The projector refuses either contradiction.
   */
  isGuest: boolean;
  /** Present if and only if `isGuest` is false. */
  profileId?: ProfileId;
  /**
   * Which *generation* of that profile id this was (S1-8). Present if and only if `isGuest` is
   * false.
   *
   * `saveChild` upserts on a caller-supplied id, so a profile that is deleted and recreated under
   * the same id is a different child wearing the old identity — and the resolver's existence check
   * and the resume guard both passed, handing child A's session, score, remaining evidence and
   * progression to child B. The profile's own `createdAt` is the cheapest thing that distinguishes
   * them, and it is data the profile already stores, so D-054(2)'s "profile ids only, nothing
   * beyond what profiles already store" is untouched: no new fact about a child is persisted here,
   * only a copy of one that already exists.
   */
  profileCreatedAt?: string;
}

export interface LaunchIdentityRecord {
  gameId: GameId;
  playerMode: PlayerMode;
  /** One or two seats, in lane order: Hero 1 left, Hero 2 right. */
  players: LaunchIdentitySeat[];
}
